import { Alert, Button, Form, Input, Select, Space, Tag } from "antd";
import type {
    ProductOperationVo,
    ProductPagingParams,
    ProductStatisticAllParams,
    ProductStatisticAllVo
} from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { PRODUCT_TYPE } from "@zhihaoscm/meta";
import {
    Button as ZhButton,
    DateRangePicker as ZhDateRangePicker,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { dashboardService } from "@/api/services/dashboardService";
import { useUserStore } from "@/stores/react/authStore";
import { formatTimeEn } from "@/utils/formatter";
import { formatNumber, toThousand } from "@/utils/number";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import Fullscreen from "@/views/components/Fullscreen/Fullscreen";
import DataCard from "@/views/components/DataCard/DataCard";
import CollectorsTableDialog from "./CollectorsTableDialog";
import UserGoodsStatisticsDialog from "../components/UserGoodsStatisticsDialog";
import UserGoodsStatisticsForVisitorDialog from "../components/UserGoodsStatisticsForVisitorDialog";

export interface GoodsOperationAnalysisQuery extends Omit<ProductPagingParams, "page" | "size"> {
    page: string;
    size: string;
    beginTime?: string;
    endTime?: string;
}

export interface GoodsOperationAnalysisPageProps {
    loadStatistics?: (params: ProductStatisticAllParams) => Promise<Partial<ProductStatisticAllVo>>;
    loadProducts?: (params: ProductPagingParams & { beginTime?: string; endTime?: string }) => Promise<{
        list?: ProductOperationVo[];
        total?: number;
    }>;
    exportProducts?: (
        params: ProductStatisticAllParams & { showColumns: string[] },
        filename: string
    ) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 商品运营统计接口，保持 /product/statistic-all 参数契约。 */
export async function loadGoodsOperationStatistics(params: ProductStatisticAllParams) {
    return dashboardService.getProductStatisticAll(params);
}

/** 复用 Vue 商品运营分页接口，保持 /product/paging 的 page/size 字符串格式。 */
export async function loadGoodsOperationProducts(
    params: ProductPagingParams & { beginTime?: string; endTime?: string }
) {
    return dashboardService.getProductPaging(params);
}

/** 将空筛选值从查询参数中剔除，避免改变 Vue 页面原有请求语义。 */
const compactParams = <T extends Record<string, unknown>>(params: T) =>
    Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== "" && value !== null)
    ) as T;

const defaultQuery: GoodsOperationAnalysisQuery = { page: "1", size: "20", scope: 2 };

const statisticItems: Array<{
    key: keyof ProductStatisticAllVo;
    label: string;
    suffix?: string;
    detail: "user" | "visitor";
}> = [
    { key: "totalSalesAmount", label: "总销售额", suffix: "元", detail: "user" },
    { key: "settlementAmount", label: "结算金额", suffix: "元", detail: "user" },
    { key: "refundAmount", label: "退款金额", suffix: "元", detail: "user" },
    { key: "totalSalesVolume", label: "总销量", suffix: "吨", detail: "user" },
    { key: "actualSettledTons", label: "实际结算吨数", suffix: "吨", detail: "user" },
    { key: "productVisitorCount", label: "商品访问人数", detail: "visitor" },
    { key: "productVisitCount", label: "商品访问次数", detail: "user" },
    { key: "avgUserStayTime", label: "平均用户停留时长", suffix: "秒", detail: "user" }
];

const dateScopeOptions = [
    { label: "全部", value: 1 },
    { label: "今日", value: 6 },
    { label: "近一周", value: 5 },
    { label: "近一月", value: 2 },
    { label: "近一季度", value: 3 },
    { label: "近一年", value: 4 }
];

/** 还原旧 Vue 分析板块标题、日期和蓝色引导线。 */
function AnalysisPanel({
    title,
    date,
    testId,
    children
}: {
    title: string;
    date?: string;
    testId?: string;
    children: ReactNode;
}) {
    return (
        <section className="goods-operation-panel" data-testid={testId}>
            <header className="goods-operation-panel__header">
                <span
                    data-testid={testId ? "goods-operation-panel-title-mark" : undefined}
                    className="goods-operation-panel__mark"
                />
                <span>{title}</span>
                {date ? <span className="goods-operation-panel__date">{date}</span> : null}
            </header>
            {children}
        </section>
    );
}

/** 用真实 SVG 结构承载四层转化数据，与旧 Vue 漏斗的颜色和层级一致。 */
function GoodsConversionFunnel({ stages = [] }: { stages?: ProductStatisticAllVo["funnelStages"] }) {
    const colors = ["#0e88ff", "#65c730", "#ffbf00", "#fb6f12"];
    return (
        <div className="goods-operation-funnel">
            <svg role="img" aria-label="商品交易转化漏斗" viewBox="0 0 320 250" className="goods-operation-funnel__svg">
                <defs>
                    <linearGradient id="funnel-green" x1="0" y1="0" x2="0" y2="1">
                        <stop stopColor="#fff" />
                        <stop offset="1" stopColor="#cdf5b8" />
                    </linearGradient>
                    <linearGradient id="funnel-yellow" x1="0" y1="0" x2="0" y2="1">
                        <stop stopColor="#fff" />
                        <stop offset="1" stopColor="#fff1c7" />
                    </linearGradient>
                </defs>
                <ellipse cx="160" cy="38" rx="150" ry="36" fill="#e4f1ff" stroke="#61a6e9" strokeWidth="2" />
                <path d="M10 38 Q160 105 310 38 L270 116 Q160 150 50 116 Z" fill="url(#funnel-green)" opacity=".65" />
                <ellipse cx="160" cy="116" rx="110" ry="25" fill="transparent" stroke="#65c730" strokeWidth="2" />
                <path d="M50 116 Q160 150 270 116 L238 184 Q160 212 82 184 Z" fill="url(#funnel-yellow)" opacity=".7" />
                <ellipse cx="160" cy="184" rx="78" ry="18" fill="transparent" stroke="#ffbf00" strokeWidth="2" />
                <text x="160" y="47" textAnchor="middle" fill={colors[0]} fontSize="24" fontWeight="600">
                    {stages[0]?.userCount ?? 0}
                </text>
                <text x="160" y="123" textAnchor="middle" fill={colors[1]} fontSize="22" fontWeight="600">
                    {stages[1]?.userCount ?? 0}
                </text>
                <text x="160" y="190" textAnchor="middle" fill={colors[2]} fontSize="20" fontWeight="600">
                    {stages[2]?.userCount ?? 0}
                </text>
                <text x="160" y="230" textAnchor="middle" fill={colors[3]} fontSize="18" fontWeight="600">
                    {stages[3]?.userCount ?? 0}
                </text>
            </svg>
            <div className="goods-operation-funnel__legend">
                {dateScopeOptions.slice(0, 4).map((_item, index) => {
                    const stage = stages[index];
                    return (
                        <div
                            key={index}
                            className="goods-operation-funnel__legend-item"
                            style={{ borderColor: colors[index] }}
                        >
                            <strong style={{ color: colors[index] }}>
                                {stage?.stageName || ["访问系统", "访问商品", "确认订单页面", "完成下单"][index]}
                            </strong>
                            {index ? <small>转化率：{String(stage?.conversionRate ?? 0)}%</small> : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/** Dashboard 商品运营分析 React 页面，保留 Vue 首屏统计、查询、分页和详情跳转。 */
export function GoodsOperationAnalysisPage({
    loadStatistics = loadGoodsOperationStatistics,
    loadProducts = loadGoodsOperationProducts,
    exportProducts = dashboardService.exportProductOperation.bind(dashboardService),
    permissions
}: GoodsOperationAnalysisPageProps) {
    const [query, setQuery] = useState<GoodsOperationAnalysisQuery>(defaultQuery);
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const [form] = Form.useForm<
        Pick<GoodsOperationAnalysisQuery, "keyword" | "productTypeId" | "supplierId" | "state" | "productType"> & {
            time?: [string | null, string | null];
        }
    >();
    const [statistics, setStatistics] = useState<Partial<ProductStatisticAllVo>>({});
    const [products, setProducts] = useState<ProductOperationVo[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [exporting, setExporting] = useState(false);
    const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
    // 保存当前商品，供旧 Vue 收藏明细弹窗按商品编号加载记录。
    const [collectorsRow, setCollectorsRow] = useState<ProductOperationVo | null>(null);
    // 记录当前统计卡片打开的明细类型，沿用 Vue 的用户/访客两个独立弹窗。
    const [statisticsDetail, setStatisticsDetail] = useState<"user" | "visitor" | null>(null);

    const canRead =
        permissions === undefined || permissions.length === 0 || permissions.includes("ROLE_SAND_PRODUCT_BI_R");
    // 对齐 Vue 搜索栏的 v-auth：导出入口只对拥有独立导出权限的用户开放。
    const canExport = (permissions ?? storePermissions).includes("ROLE_SAND_PRODUCT_BI_EXPORT");
    const statisticParams = useMemo(() => {
        // 统计接口不接受分页字段，按 Vue 页面语义只复制非分页参数。
        const params = Object.fromEntries(Object.entries(query).filter(([key]) => key !== "page" && key !== "size"));
        return compactParams(params) as ProductStatisticAllParams;
    }, [query]);

    useEffect(() => {
        if (!canRead) return;
        let disposed = false;
        setErrorMessage("");
        // 统计卡片和明细列表是 Vue 首屏的两条独立请求，保持并行加载以减少等待时间。
        void Promise.all([loadStatistics(statisticParams), loadProducts(query)])
            .then(([summary, result]) => {
                if (disposed) return;
                setStatistics(summary || {});
                setProducts(result?.list || []);
                setTotal(result?.total || 0);
            })
            .catch(() => {
                if (disposed) return;
                setStatistics({});
                setProducts([]);
                setTotal(0);
                setErrorMessage("商品运营分析加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [canRead, loadProducts, loadStatistics, query, statisticParams]);

    const submitFilters = (values: Record<string, unknown>) => {
        const [beginDate, endDate] = dateRange;
        setQuery({
            page: "1",
            size: "20",
            scope: query.scope,
            ...compactParams({
                ...values,
                beginTime: beginDate ? `${beginDate} 00:00:00` : undefined,
                endTime: endDate ? `${endDate} 23:59:59` : undefined
            })
        } as GoodsOperationAnalysisQuery);
    };

    const resetFilters = () => {
        form.resetFields();
        setDateRange([null, null]);
        setQuery(defaultQuery);
    };

    const exportCurrent = async () => {
        setExporting(true);
        try {
            await exportProducts(
                { ...statisticParams, showColumns: ["productId", "title", "state", "updateTime"] },
                `商品运营分析${new Date().toISOString().slice(0, 10).replaceAll("-", "")}`
            );
        } catch {
            setErrorMessage("商品运营分析导出失败");
        } finally {
            setExporting(false);
        }
    };

    // 保留旧 Vue 明细表的 custom sort：排序字段直接使用列 prop，并把分页重置到第一页。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            setQuery(current => {
                const nextQuery = { ...current, page: "1" };
                if (!prop || !order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = prop;
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 明细列沿用 Vue 表格字段和展示规则，避免迁移后改变详情跳转及状态文案。
    const productColumns: TableColumnProps<ProductOperationVo>[] = [
        {
            label: "商品编号",
            prop: "productId",
            render: ({ row }) =>
                row.productType === 1 ? (
                    <Link to={`/dashboard/consignmentGoodsAnalysisDetail?id=${row.productId}`}>{row.productId}</Link>
                ) : (
                    row.productId || "--"
                )
        },
        {
            label: "主图/标题",
            prop: "title",
            render: ({ row }) => (
                <Space>
                    <span>{row.title || "--"}</span>
                    {row.isTop ? <Tag color="blue">置顶</Tag> : null}
                </Space>
            )
        },
        { label: "品类", prop: "productTypeName" },
        { label: "供应商", prop: "supplierName" },
        {
            label: "商品状态",
            prop: "state",
            render: ({ row }) => (row.state === 1 ? "上架" : row.state === 2 ? "下架" : (row.state ?? "--"))
        },
        { label: "更新时间", prop: "updateTime", sortable: "custom" },
        {
            label: "平均用户停留时长",
            prop: "avgUserStayTime",
            sortable: "custom",
            render: ({ row }) => formatTimeEn(row.avgUserStayTime)
        },
        {
            label: "平均单次停留时长",
            prop: "avgSingleStayTime",
            sortable: "custom",
            render: ({ row }) => formatTimeEn(row.avgSingleStayTime)
        },
        {
            label: "商品访问人数",
            prop: "productVisitorCount",
            sortable: "custom",
            render: ({ row }) => toThousand(row.productVisitorCount)
        },
        {
            label: "商品访问次数",
            prop: "productVisitCount",
            sortable: "custom",
            render: ({ row }) => toThousand(row.productVisitCount)
        },
        {
            label: "总销售额(元)",
            prop: "totalSalesAmount",
            sortable: "custom",
            render: ({ row }) => formatNumber(row.totalSalesAmount, 2)
        },
        {
            label: "结算金额(元)",
            prop: "settlementAmount",
            sortable: "custom",
            render: ({ row }) => formatNumber(row.settlementAmount, 2)
        },
        {
            label: "退款金额(元)",
            prop: "refundAmount",
            sortable: "custom",
            render: ({ row }) => formatNumber(row.refundAmount, 2)
        },
        {
            label: "总销量(吨)",
            prop: "totalSalesVolume",
            sortable: "custom",
            render: ({ row }) => formatNumber(row.totalSalesVolume, 2)
        },
        {
            label: "实际结算吨数(吨)",
            prop: "actualSettledTons",
            sortable: "custom",
            render: ({ row }) => formatNumber(row.actualSettledTons, 2)
        },
        {
            label: "收藏人数",
            prop: "favoriteCount",
            render: ({ row }) =>
                row.favoriteCount ? (
                    <Button
                        type="link"
                        aria-label={`查看收藏明细 ${row.favoriteCount}`}
                        onClick={() => setCollectorsRow(row)}
                    >
                        {row.favoriteCount}
                    </Button>
                ) : (
                    row.favoriteCount
                )
        },
        {
            label: "商品类型",
            prop: "productType",
            render: ({ row }) => PRODUCT_TYPE.getIntroByCode(row.productType) || "--"
        }
    ];

    if (!canRead) {
        return <Alert aria-label="goods-operation-analysis-page" message="暂无商品运营分析权限" type="warning" />;
    }

    const dateLabel =
        query.beginTime && query.endTime ? `${query.beginTime.slice(0, 10)}-${query.endTime.slice(0, 10)}` : "";

    return (
        <section
            id="goods-operation-analysis-content"
            aria-label="goods-operation-analysis-page"
            className="goods-operation-analysis-page flex min-h-full flex-col gap-3"
        >
            <section className="goods-operation-toolbar">
                <h1 className="goods-operation-title">商品运营分析</h1>
                <div className="goods-operation-toolbar__main">
                    <Form className="goods-operation-filter-form" form={form} layout="inline" onFinish={submitFilters}>
                        {canExport ? (
                            <ZhButton color="primary" loading={exporting} onClick={exportCurrent}>
                                导出
                            </ZhButton>
                        ) : null}
                        <Form.Item name="keyword">
                            <Input aria-label="商品编号或商品标题" placeholder="请输入商品编号或商品标题" allowClear />
                        </Form.Item>
                        <Form.Item name="productTypeId">
                            <Input aria-label="品类" placeholder="请选择品类" />
                        </Form.Item>
                        <Form.Item name="supplierId">
                            <Input aria-label="供应商" placeholder="请选择供应商" />
                        </Form.Item>
                        <Form.Item name="state">
                            <Select
                                aria-label="商品状态"
                                allowClear
                                style={{ width: 120 }}
                                options={[
                                    { label: "上架", value: 1 },
                                    { label: "下架", value: 2 }
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="productType">
                            <Select
                                aria-label="商品类型"
                                allowClear
                                style={{ width: 140 }}
                                options={PRODUCT_TYPE.options.map(option => ({
                                    label: option.label,
                                    value: option.value
                                }))}
                            />
                        </Form.Item>
                        <Form.Item>
                            <ZhDateRangePicker
                                format="YYYY-MM-DD"
                                startPlaceholder="开始日期"
                                endPlaceholder="结束日期"
                                type="daterange"
                                value={dateRange}
                                valueFormat="YYYY-MM-DD"
                                onChange={(_value, formattedValue) => {
                                    const nextRange: [string | null, string | null] = formattedValue
                                        ? [formattedValue[0] || null, formattedValue[1] || null]
                                        : [null, null];
                                    setDateRange(nextRange);
                                    form.setFieldValue("time", nextRange);
                                }}
                            />
                        </Form.Item>
                        <Space size={4}>
                            <ZhButton
                                aria-label="搜索"
                                color="primary"
                                htmlType="submit"
                                icon={<TableSearchIcon name="search" />}
                            >
                                搜索
                            </ZhButton>
                            <ZhButton
                                aria-label="重置"
                                icon={<TableSearchIcon name="reset" />}
                                iconOnly
                                onClick={resetFilters}
                                title="重置"
                            />
                        </Space>
                    </Form>
                    <Fullscreen targetId="goods-operation-analysis-content" />
                </div>
                <div className="goods-operation-date-scopes" role="group" aria-label="快捷日期">
                    {dateScopeOptions.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            aria-pressed={query.scope === option.value}
                            className={query.scope === option.value ? "is-active" : undefined}
                            onClick={() => setQuery(current => ({ ...current, page: "1", scope: option.value }))}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </section>
            {errorMessage ? <Alert role="alert" message={errorMessage} type="error" /> : null}
            <AnalysisPanel title="商品运营统计" date={dateLabel} testId="goods-operation-statistics-panel">
                <div className="goods-operation-statistics-grid">
                    {statisticItems.map(item => (
                        <button
                            key={item.key}
                            type="button"
                            className="goods-operation-statistics-button"
                            aria-label={`查看${item.label}${item.detail === "visitor" ? "访客" : "用户"}明细`}
                            onClick={() => setStatisticsDetail(item.detail)}
                        >
                            <DataCard
                                title={`${item.label}${item.suffix ? `(${item.suffix})` : ""}`}
                                data={(statistics[item.key] as number | undefined) ?? 0}
                            />
                        </button>
                    ))}
                </div>
            </AnalysisPanel>
            <AnalysisPanel title="商品交易转化" date={dateLabel}>
                <GoodsConversionFunnel stages={statistics.funnelStages} />
            </AnalysisPanel>
            <AnalysisPanel title="商品运营明细" date={dateLabel}>
                <ZhTable<ProductOperationVo>
                    rowKey={row => String(row.productId)}
                    data={products}
                    columns={productColumns}
                    emptyText="暂无数据"
                    pagination={false}
                    onSortChange={handleSortChange}
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4 self-end"
                                current={Number(query.page)}
                                pageSize={Number(query.size)}
                                total={total}
                                showSizeChanger={false}
                                onChange={page => setQuery(current => ({ ...current, page: String(page) }))}
                            />
                        </div>
                    }
                />
            </AnalysisPanel>
            <CollectorsTableDialog
                open={collectorsRow !== null}
                productId={collectorsRow?.productId || ""}
                title={collectorsRow?.title}
                onClose={() => setCollectorsRow(null)}
            />
            <UserGoodsStatisticsDialog
                open={statisticsDetail === "user"}
                baseParams={statisticParams}
                onClose={() => setStatisticsDetail(null)}
            />
            <UserGoodsStatisticsForVisitorDialog
                open={statisticsDetail === "visitor"}
                baseParams={statisticParams}
                onClose={() => setStatisticsDetail(null)}
            />
        </section>
    );
}

export default GoodsOperationAnalysisPage;
